import React, { useEffect, useState, useRef, useCallback } from "react";
import { Card, List, Button, Modal, Form, Input, Select, Checkbox, message, Tag, Space, Pagination, Spin, Empty, Tooltip, Upload, Image } from "antd";
import { ReloadOutlined, MessageOutlined, SearchOutlined, EyeOutlined, StarFilled, UploadOutlined } from "@ant-design/icons";
import { db } from "../../firebase/firebase";
import { collection, getDocs, doc, addDoc, query, where, limit, DocumentSnapshot, onSnapshot, Unsubscribe, arrayUnion, getDoc, updateDoc } from "firebase/firestore";
import { Device, BBSPost, BBSSettings, BBSPostFormData, BBSPostWithReads, BBSPostRead } from "./web_parts/types";
import { useAuth } from "../../context/AuthContext";
import type { UploadProps } from 'antd';


// 新着アイコン用の脈動アニメーション
const pulseKeyframes = `
  @keyframes pulse {
    0% { box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.7); }
    70% { box-shadow: 0 0 0 10px rgba(255, 77, 79, 0); }
    100% { box-shadow: 0 0 0 0 rgba(255, 77, 79, 0); }
  }
  
  @keyframes bounce {
    0%, 20%, 53%, 80%, 100% { transform: translate3d(0,0,0); }
    40%, 43% { transform: translate3d(0, -8px, 0); }
    70% { transform: translate3d(0, -4px, 0); }
    90% { transform: translate3d(0, -1px, 0); }
  }

  .new-post-icon {
    animation: bounce 2s infinite;
    color: #ff4d4f;
    font-size: 16px;
    margin-right: 8px;
  }

  .new-post-pulse {
    animation: pulse 2s infinite;
  }
`;

// スタイルをページに注入
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = pulseKeyframes;
  if (!document.head.querySelector('style[data-bbs-pulse]')) {
    style.setAttribute('data-bbs-pulse', 'true');
    document.head.appendChild(style);
  }
}

const { TextArea } = Input;
const { Option } = Select;

interface BBSProps {
  device: Device;
  onClose: () => void;
}

const BBS: React.FC<BBSProps> = ({ device, onClose }) => {
  // デバイス識別子: Storage ルールは devices/{docId} を参照するためドキュメント ID を優先
  const deviceKey = device.id || device.serialNumber || (device as { deviceId?: string }).deviceId || "";

  // AuthContextからログイン情報を取得
  const { currentUser } = useAuth();

  // State管理
  const [bbsPosts, setBbsPosts] = useState<BBSPostWithReads[]>([]);
  const [bbsSettings, setBbsSettings] = useState<BBSSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postSubmitting, setPostSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isSearchExecuted, setIsSearchExecuted] = useState(false);
  const [pageCursors, setPageCursors] = useState<{ [page: number]: DocumentSnapshot | null }>({});

  const [trulyNewPosts, setTrulyNewPosts] = useState<Set<string>>(new Set());
  const isInitialLoadComplete = useRef(false);
  const readMarkInProgress = useRef(false);

  // Modal制御
  const [isPostModalVisible, setIsPostModalVisible] = useState(false);

  // メディアファイルアップロード用のState
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageUploading, setImageUploading] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage, setPostsPerPage] = useState(10);
  const [totalPosts, setTotalPosts] = useState(0);

  // Forms
  const [postForm] = Form.useForm();

  // リアルタイム更新用のUnsubscribe関数
  const unsubscribePostsRef = useRef<Unsubscribe | null>(null);

  const isLoadingPage = useRef(false);

  // 現在のログインユーザー情報を取得
  const getCurrentUser = () => {
    if (!currentUser) {
      console.log("ログインユーザーが見つかりません");
      return null;
    }

    return {
      id: currentUser.uid,
      email: currentUser.email || "",
      displayName: currentUser.displayName || currentUser.email?.split('@')[0] || "ユーザー"
    };
  };

  const isNewPost = (postId: string): boolean => {
    return trulyNewPosts.has(postId);
  };

  const processPostsWithReads = useCallback((posts: BBSPost[]): BBSPostWithReads[] => {
    const currentUser = getCurrentUser();
    return posts.map((post) => {
      const readBy = post.readBy || [];
      const isReadByCurrentUser = currentUser ? readBy.some((read: BBSPostRead) => read.userEmail === currentUser.email) : false;
      const readsExcludingAuthor = readBy.filter((read: BBSPostRead) => read.userEmail !== currentUser?.email);
      return {
        ...post,
        reads: readBy,
        readCount: readsExcludingAuthor.length,
        isReadByCurrentUser: isReadByCurrentUser,
        readBy
      };
    });
  }, [currentUser]);

  const updateTotalPostsCount = useCallback(async () => {
    if (!deviceKey) return;
    try {
      const countQuery = query(
        collection(db, "bbs_posts"),
        where("serialNumber", "==", deviceKey)
      );
      const countSnapshot = await getDocs(countQuery);
      const count = countSnapshot.docs.filter(d => d.data().isDeleted !== true).length;
      setTotalPosts(count);
    } catch (error) {
      console.error("総投稿数取得エラー:", error);
    }
  }, [deviceKey]);

  const setupPostsRealTimeListener = useCallback(async () => {
    if (!deviceKey) return;
    try {
      if (unsubscribePostsRef.current) {
        unsubscribePostsRef.current();
        unsubscribePostsRef.current = null;
      }
      // 単一フィールドクエリで複合インデックス不要（isDeleted・createdAtはメモリで処理）
      const postsQuery = query(
        collection(db, "bbs_posts"),
        where("serialNumber", "==", deviceKey)
      );
      const unsubscribe = onSnapshot(postsQuery, async (snapshot) => {
        if (currentPage !== 1) {
          return;
        }
        let posts = snapshot.docs
          .map(d => ({ id: d.id, ...d.data() })) as BBSPost[];
        posts = posts.filter(p => p.isDeleted !== true);
        posts.sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
        posts = posts.slice(0, postsPerPage);
        const postsWithReads = processPostsWithReads(posts);
        if (!isInitialLoadComplete.current) {
          const unreadPostIds = postsWithReads
            .filter(post => !post.isReadByCurrentUser)
            .map(post => post.id);
          setTrulyNewPosts(new Set(unreadPostIds));
          isInitialLoadComplete.current = true;
          setTimeout(async () => {
            if (unreadPostIds.length > 0) {
              readMarkInProgress.current = true;
              await markDisplayedPostsAsRead(postsWithReads.filter(post => unreadPostIds.includes(post.id)));
              readMarkInProgress.current = false;
            }
          }, 2000);
        } else if (!readMarkInProgress.current) {
          setTrulyNewPosts(prevNewPosts => {
            const newPostsFound = postsWithReads.filter(p => !p.isReadByCurrentUser && !prevNewPosts.has(p.id));
            if (newPostsFound.length > 0) {
              const newSet = new Set(prevNewPosts);
              newPostsFound.forEach(p => newSet.add(p.id));
              return newSet;
            }
            return prevNewPosts;
          });
        }
        setBbsPosts(postsWithReads);
        if (snapshot.docs.length > 0) {
          const lastDoc = snapshot.docs[snapshot.docs.length - 1];
          setPageCursors({ 2: lastDoc });
        }
        await updateTotalPostsCount();
      }, (error) => {
        console.error("投稿リアルタイム更新エラー:", error);
      });
      unsubscribePostsRef.current = unsubscribe;
    } catch (error) {
      console.error("投稿リアルタイムリスナー設定エラー:", error);
    }
  }, [deviceKey, postsPerPage, currentPage, processPostsWithReads, updateTotalPostsCount]);

  const loadPosts = useCallback(async (page: number, size: number) => {
    if (!deviceKey || isLoadingPage.current) return;

    isLoadingPage.current = true;
    setPostsLoading(true);

    try {
      const postsQuery = query(
        collection(db, "bbs_posts"),
        where("serialNumber", "==", deviceKey)
      );
      const snapshot = await getDocs(postsQuery);
      let postsData = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() })) as BBSPost[];
      postsData = postsData.filter(p => p.isDeleted !== true);
      postsData.sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
      const startIdx = (page - 1) * size;
      postsData = postsData.slice(startIdx, startIdx + size);

      const postsWithReads = processPostsWithReads(postsData);
      setBbsPosts(postsWithReads);

      await updateTotalPostsCount();
    } catch (error) {
      console.error("投稿読み込みエラー:", error);
      message.error("投稿の読み込みに失敗しました");
    } finally {
      setPostsLoading(false);
      isLoadingPage.current = false;
    }
  }, [deviceKey, processPostsWithReads, updateTotalPostsCount]);

  useEffect(() => {
    loadInitialData();
    return () => {
      if (unsubscribePostsRef.current) {
        unsubscribePostsRef.current();
      }
    };
  }, [deviceKey]);

  useEffect(() => {
    if (isSearchExecuted) {
      return;
    }
    if (currentPage === 1 && !unsubscribePostsRef.current) {
      setupPostsRealTimeListener();
    } else if (currentPage > 1) {
      if (unsubscribePostsRef.current) {
        unsubscribePostsRef.current();
        unsubscribePostsRef.current = null;
      }
      loadPosts(currentPage, postsPerPage);
    }
  }, [currentPage, postsPerPage]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      setCurrentPage(1);
      setPageCursors({});
      resetNewState();
      await loadBBSSettings();
      await setupPostsRealTimeListener();
    } catch (error) {
      console.error("データ読み込みエラー:", error);
      message.error("データの読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const loadBBSSettings = async () => {
    if (!deviceKey) return;
    try {
      const settingsQuery = query(
        collection(db, "bbs_settings"),
        where("serialNumber", "==", deviceKey),
        limit(1)
      );
      const snapshot = await getDocs(settingsQuery);
      if (!snapshot.empty) {
        const settingsData = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as BBSSettings;
        setBbsSettings(settingsData);
      } else {
        await createDefaultSettings();
      }
    } catch (error) {
      console.error("BBS設定読み込みエラー:", error);
    }
  };

  const markDisplayedPostsAsRead = async (posts: BBSPostWithReads[]) => {
    const currentUser = getCurrentUser();
    if (!currentUser?.email) return;
    try {
      const unreadPosts = posts.filter(post => !post.isReadByCurrentUser);
      if (unreadPosts.length === 0) return;

      // ログインユーザー情報（bbs_users未登録でもdisplayNameを使用）
      const userName = currentUser.displayName;
      const userType = 'general';

      const readPromises = unreadPosts.map(async (post) => {
        try {
          const postRef = doc(db, "bbs_posts", post.id);
          const postDoc = await getDoc(postRef);
          if (!postDoc.exists()) return;
          const postData = postDoc.data() as BBSPost;
          const existingReadBy = postData.readBy || [];
          const alreadyRead = existingReadBy.some((read: BBSPostRead) => read.userEmail === currentUser.email);
          if (alreadyRead) return;
          const readData: BBSPostRead = {
            userEmail: currentUser.email,
            userName,
            userType,
            readAt: Date.now()
          };
          await updateDoc(postRef, { readBy: arrayUnion(readData) });
        } catch (error) {
          console.warn(`投稿 ${post.id} の既読マーク追加エラー:`, error);
        }
      });
      await Promise.all(readPromises);
    } catch (error) {
      console.error("既読マーク処理エラー:", error);
    }
  };

  const resetNewState = () => {
    setTrulyNewPosts(new Set());
    isInitialLoadComplete.current = false;
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      resetNewState();
      await setupPostsRealTimeListener();
      return;
    }
    if (!deviceKey) return;
    setIsSearchExecuted(true);
    setPostsLoading(true);
    try {
      const allPostsQuery = query(
        collection(db, "bbs_posts"),
        where("serialNumber", "==", deviceKey)
      );
      const snapshot = await getDocs(allPostsQuery);
      let allPosts = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() })) as BBSPost[];
      allPosts = allPosts.filter(p => p.isDeleted !== true);
      allPosts.sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
      const searchTermLower = searchTerm.toLowerCase();
      const filteredPosts = allPosts.filter(post => 
        post.content?.toLowerCase().includes(searchTermLower) ||
        post.authorName?.toLowerCase().includes(searchTermLower)
      );
      const startIndex = (currentPage - 1) * postsPerPage;
      const paginatedPosts = filteredPosts.slice(startIndex, startIndex + postsPerPage);
      const postsWithReads = processPostsWithReads(paginatedPosts);
      setBbsPosts(postsWithReads);
      setTotalPosts(filteredPosts.length);
    } catch (error) {
      console.error("検索エラー:", error);
      message.error("検索に失敗しました");
    } finally {
      setPostsLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setIsSearchExecuted(false);
    setCurrentPage(1);
    resetNewState();
    setupPostsRealTimeListener();
  };
  
  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (isSearchExecuted) setIsSearchExecuted(false);
  };

  const getReadInfoDisplay = (post: BBSPostWithReads) => {
    const currentUser = getCurrentUser();
    const readBy = post.readBy || [];
    if (readBy.length === 0) return "未読";
    const readUsersExcludingAuthor = readBy.filter((read: BBSPostRead) => read.userEmail !== currentUser?.email);
    if (readUsersExcludingAuthor.length === 0) return "未読";
    if (readUsersExcludingAuthor.length <= 2) {
      return readUsersExcludingAuthor.map(read => read.userName).join(", ");
    } else {
      const displayUsers = readUsersExcludingAuthor.slice(0, 2).map(read => read.userName);
      return `${displayUsers.join(", ")} 他${readUsersExcludingAuthor.length - 2}名`;
    }
  };

  const getReadUsersDetail = (post: BBSPostWithReads) => {
    const currentUser = getCurrentUser();
    const readBy = post.readBy || [];
    if (readBy.length === 0) return "まだ誰も読んでいません";
    const readUsersExcludingAuthor = readBy.filter((read: BBSPostRead) => read.userEmail !== currentUser?.email);
    if (readUsersExcludingAuthor.length === 0) return "まだ誰も読んでいません";
    return readUsersExcludingAuthor.map(read => `${read.userName} (${getUserTypeLabel(read.userType)})`).join(", ");
  };

  const getReadCount = (post: BBSPostWithReads) => {
    const currentUser = getCurrentUser();
    const readBy = post.readBy || [];
    if (readBy.length === 0) return 0;
    return readBy.filter((read: BBSPostRead) => read.userEmail !== currentUser?.email).length;
  };

  const createDefaultSettings = async () => {
    const currentUser = getCurrentUser();
    if (!currentUser || !deviceKey) return;
    const defaultSettings: Omit<BBSSettings, "id"> = {
      serialNumber: deviceKey,
      boardName: `${device.deviceName || device.serialNumber}の掲示板`,
      description: "このデバイス専用の掲示板です",
      isActive: true,
      maxDisplayPosts: 10,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      createdBy: currentUser.id
    };
    const docRef = await addDoc(collection(db, "bbs_settings"), defaultSettings);
    setBbsSettings({ ...defaultSettings, id: docRef.id });
  };

  const handleSavePost = async (values: BBSPostFormData) => {
    if (postSubmitting) return;
    setPostSubmitting(true);
    setImageUploading(!!imageFile);
    try {
      const poster = getCurrentUser();
      if (!poster || !currentUser) {
        message.error("ログインが必要です");
        setPostSubmitting(false);
        setImageUploading(false);
        return;
      }

      let imageUrl: string | null = null;
      
      if (imageFile) {
        message.loading({ content: 'メディアをアップロード中...', key: 'uploading' });
        try {
          const safeDeviceKey = (deviceKey || 'default').replace(/[\/\\#?]/g, '_');
          const idTokenForUpload = await currentUser.getIdToken();
          const qs = new URLSearchParams({
            deviceId: safeDeviceKey,
            fileName: imageFile.name || 'upload.bin',
          });
          const uploadRes = await fetch(`/api/uploadBbsMedia?${qs.toString()}`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${idTokenForUpload}`,
              'Content-Type': imageFile.type || 'application/octet-stream',
            },
            body: imageFile,
          });
          let uploadJson: {
            success?: boolean;
            downloadUrl?: string;
            error?: string;
          } = {};
          try {
            uploadJson = await uploadRes.json() as typeof uploadJson;
          } catch {
            message.destroy('uploading');
            message.error(`画像のアップロードに失敗しました（HTTP ${uploadRes.status}）`);
            setPostSubmitting(false);
            setImageUploading(false);
            return;
          }
          if (!uploadRes.ok || !uploadJson.success || !uploadJson.downloadUrl) {
            message.destroy('uploading');
            const errMsg = uploadJson.error || '';
            message.error(
              uploadRes.status === 401 || uploadRes.status === 403 || /権限|認証/.test(errMsg)
                ? '画像のアップロードに失敗しました。ログイン状態とアクセス権限を確認してください。'
                : (errMsg || `画像のアップロードに失敗しました（HTTP ${uploadRes.status}）`)
            );
            setPostSubmitting(false);
            setImageUploading(false);
            return;
          }
          imageUrl = uploadJson.downloadUrl;
          message.success({ content: 'アップロードが完了しました', key: 'uploading', duration: 2 });
        } catch (uploadError: unknown) {
          message.destroy('uploading');
          const err = uploadError as { code?: string; message?: string };
          console.error('メディアアップロードエラー:', uploadError);
          const errMsg = err?.message || String(uploadError);
          const isPerm = /permission|403|401|402|unauthorized|quota|billing/i.test(errMsg);
          message.error(
            isPerm
              ? '画像のアップロードに失敗しました。ログイン状態とStorageのアクセス権限を確認してください。'
              : `画像のアップロードに失敗しました: ${errMsg.substring(0, 80)}`
          );
          setPostSubmitting(false);
          setImageUploading(false);
          return;
        }
      }

      const idToken = await currentUser.getIdToken();
      if (!idToken) {
        message.error("認証トークンを取得できませんでした。再ログインしてください。");
        setPostSubmitting(false);
        setImageUploading(false);
        return;
      }

      const response = await fetch('/api/createBBSPost', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          idToken,
          serialNumber: deviceKey,
          authorEmail: poster.email,
          authorName: poster.displayName,
          authorType: 'general',
          content: values.content,
          sendEmailNotification: values.sendEmailNotification,
          source: 'web',
          imageUrl: imageUrl,
          mediaType: imageFile ? imageFile.type : null, 
        }),
      });

      const result = await response.json();
      if (response.ok && result.success) {
        message.success("投稿しました");
        setIsPostModalVisible(false);
        postForm.resetFields();
        setImageFile(null);
        if(imagePreview) URL.revokeObjectURL(imagePreview);
        setImagePreview(null);

        // 投稿成功後、掲示板を強制更新して新しい投稿を表示
        resetNewState();
        await setupPostsRealTimeListener();
        // 掲示板投稿の FCM は Cloud Functions createBBSPost 内で送信（Android 投稿と重複しないよう一元化）
      } else {
        message.error(result.error || "投稿に失敗しました");
      }
    } catch (error) {
      message.destroy('uploading');
      console.error("投稿処理エラー:", error);
      message.error("投稿に失敗しました");
    } finally {
      setPostSubmitting(false);
      setImageUploading(false);
    }
  };

  const getFormattedTime = (timestamp: number) => {
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}/${month}/${day} ${hours}:${minutes}`;
  };

  const handleImageChange: UploadProps['beforeUpload'] = (file) => {
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');

    if (!isImage && !isVideo) {
      message.error('画像または動画ファイルのみアップロードできます');
      return Upload.LIST_IGNORE;
    }

    const isLt100M = file.size / 1024 / 1024 < 100;
    if (!isLt100M) {
      message.error('ファイルサイズは100MB未満にしてください');
      return Upload.LIST_IGNORE;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    return false;
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
      setImagePreview(null);
    }
  };

  const getUserTypeLabel = (type: string) => {
    switch (type) {
      case 'device_owner': return 'デバイス利用者';
      case 'family': return '家族';
      case 'general': return '一般ユーザー';
      default: return type;
    }
  };

  const getUserTypeColor = (type: string) => {
    switch (type) {
      case 'device_owner': return 'blue';
      case 'family': return 'green';
      case 'general': return 'orange';
      default: return 'default';
    }
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', textAlign: 'center', marginTop: 50 }} />;
  }

  return (
    <div style={{ height: '70vh', overflow: 'auto' }}>
      {/* ヘッダー部分 */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
          <h2 style={{ margin: 0, fontSize: '18px' }}>
            {bbsSettings?.boardName || `${device.deviceName || device.serialNumber}の掲示板`}
          </h2>
          <Space wrap>
            <Button icon={<ReloadOutlined />} onClick={() => {
              setTrulyNewPosts(new Set());
              isInitialLoadComplete.current = false;
              setCurrentPage(1);
              setPageCursors({});
              setupPostsRealTimeListener();
            }}>
              更新
            </Button>
            <Button icon={<MessageOutlined />} type="primary" onClick={() => setIsPostModalVisible(true)} disabled={postSubmitting} loading={postSubmitting}>
              {postSubmitting ? "投稿中..." : "投稿する"}
            </Button>
          </Space>
        </div>
        <div style={{ fontSize: '12px', color: '#52c41a', marginTop: 4, padding: '4px 8px', backgroundColor: '#f6ffed', borderRadius: '4px', border: '1px solid #b7eb8f' }}>
          🔄 リアルタイム更新中 - 新しい投稿は自動で表示されます
        </div>
      </div>

      {/* 投稿一覧 */}
      <Card title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontSize: '14px' }}>投稿一覧</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 200 }}>
              <Input placeholder="投稿内容・投稿者名で検索" value={searchTerm} onChange={handleSearchTermChange} onKeyPress={handleSearchKeyPress} style={{ width: 200 }} allowClear onClear={handleClearSearch} />
              <Button icon={<SearchOutlined />} onClick={handleSearch} type={searchTerm ? "primary" : "default"} style={{ minWidth: 32 }} loading={postsLoading && isSearchExecuted} />
            </div>
            <Select value={postsPerPage} onChange={(value) => {
                setPostsPerPage(value);
                setCurrentPage(1);
                if (isSearchExecuted) {
                  handleSearch();
                } else {
                  setTrulyNewPosts(new Set());
                  isInitialLoadComplete.current = false;
                  setupPostsRealTimeListener();
                }
              }} style={{ minWidth: 100 }}>
              <Option value={10}>10件表示</Option>
              <Option value={50}>50件表示</Option>
              <Option value={100}>100件表示</Option>
            </Select>
          </div>
        } size="small">
        <Spin spinning={postsLoading}>
          {searchTerm && isSearchExecuted && (
            <div style={{ marginBottom: 16, padding: '8px 12px', backgroundColor: '#e6f7ff', borderRadius: '4px', border: '1px solid #91d5ff', fontSize: '14px' }}>
              <span>「{searchTerm}」の検索結果: {totalPosts}件</span>
              <Button type="link" onClick={handleClearSearch} style={{ padding: 0, marginLeft: 8, fontSize: '12px' }}>検索解除</Button>
            </div>
          )}
          {bbsPosts.length === 0 ? (
            <Empty description={searchTerm && isSearchExecuted ? "検索結果が見つかりませんでした" : "投稿がありません"} />
          ) : (
            <>
              <List
                dataSource={bbsPosts}
                renderItem={(post) => (
                  <List.Item key={post.id} style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <div style={{ width: '100%', backgroundColor: isNewPost(post.id) ? '#fffbe6' : '#fafafa', border: isNewPost(post.id) ? '2px solid #fadb14' : '1px solid #e8e8e8', borderRadius: '8px', padding: '16px', boxShadow: isNewPost(post.id) ? '0 4px 12px rgba(250, 219, 20, 0.3)' : '0 1px 3px rgba(0,0,0,0.1)', transition: 'all 0.3s ease' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                          <span style={{ fontWeight: 'bold', fontSize: '15px', color: '#333' }}>{post.authorName}</span>
                          <Tag color={getUserTypeColor(post.authorType)} style={{ fontSize: '11px', margin: 0 }}>{getUserTypeLabel(post.authorType)}</Tag>
                          {isNewPost(post.id) && (<span style={{ backgroundColor: '#ff4d4f', color: 'white', fontSize: '10px', padding: '2px 6px', borderRadius: '10px', fontWeight: 'bold', marginLeft: '4px', boxShadow: '0 2px 4px rgba(255, 77, 79, 0.3)' }}>新着</span>)}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '12px', color: '#888', textAlign: 'right', whiteSpace: 'nowrap', flexShrink: 0 }}>
                          {isNewPost(post.id) && <StarFilled className="new-post-icon" />}
                          <span>{getFormattedTime(post.createdAt)}</span>
                        </div>
                      </div>
                      <div style={{ whiteSpace: 'pre-wrap', fontSize: '14px', lineHeight: '1.6', color: '#333', marginBottom: 12, padding: '12px 0', borderBottom: '1px solid #e0e0e0' }}>
                        {post.content}
                      </div>

                      {post.imageUrl && (
                        <div style={{ marginTop: 12, paddingBottom: 12, borderBottom: '1px solid #e0e0e0' }}>
                          {post.mediaType?.startsWith('video/') ? (
                            <video
                              src={post.imageUrl}
                              controls
                              style={{ maxHeight: 300, maxWidth: '100%', borderRadius: '8px', objectFit: 'contain', display: 'block' }}
                              preload="metadata"
                            />
                          ) : (
                            <Image
                              src={post.imageUrl}
                              alt="投稿画像"
                              style={{ maxHeight: 300, maxWidth: '100%', borderRadius: '8px', objectFit: 'contain' }}
                            />
                          )}
                        </div>
                      )}

                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '12px', color: '#666', marginTop: '12px' }}>
                        <EyeOutlined style={{ color: '#1890ff', fontSize: '14px' }} />
                        <span style={{ fontWeight: '500' }}>既読:</span>
                        <Tooltip title={getReadUsersDetail(post)} placement="top">
                          <span style={{ cursor: 'help', padding: '2px 6px', backgroundColor: '#e6f7ff', borderRadius: '12px', border: '1px solid #91d5ff', fontSize: '11px' }}>
                            {getReadInfoDisplay(post)} ({getReadCount(post)}名)
                          </span>
                        </Tooltip>
                      </div>
                    </div>
                  </List.Item>
                )}
              />
              {totalPosts > postsPerPage && !isSearchExecuted && (
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                  <Pagination current={currentPage} pageSize={postsPerPage} total={totalPosts} onChange={(page) => {
                      if (page === 1) setPageCursors({});
                      setCurrentPage(page);
                    }} showSizeChanger={false} showQuickJumper={false} simple={window.innerWidth < 768} showTotal={(total, range) => `${range[0]}-${range[1]} / ${total} 件`} />
                </div>
              )}
            </>
          )}
        </Spin>
      </Card>

      {/* 投稿モーダル */}
      <Modal title="新しい投稿" visible={isPostModalVisible} onCancel={() => {
          if (!postSubmitting) {
            setIsPostModalVisible(false);
            handleRemoveImage();
            setPostSubmitting(false);
          }
        }} onOk={() => {
          if (!postSubmitting) postForm.submit();
        }} okText={postSubmitting ? (imageUploading ? "アップロード中..." : "投稿中...") : "投稿する"} cancelText="キャンセル" confirmLoading={postSubmitting} okButtonProps={{ disabled: postSubmitting }} cancelButtonProps={{ disabled: postSubmitting }} closable={!postSubmitting} maskClosable={!postSubmitting} width={Math.min(700, window.innerWidth * 0.95)} style={{ top: 20 }}>
        <Form form={postForm} layout="vertical" onFinish={handleSavePost}>
          <Form.Item
            name="content"
            label="投稿内容"
            rules={[
              {
                validator: async (_, value) => {
                  const text = typeof value === "string" ? value.trim() : "";
                  if (!text && !imageFile) {
                    return Promise.reject(new Error("投稿内容または画像・動画を指定してください"));
                  }
                  if (text.length > 1000) {
                    return Promise.reject(new Error("投稿内容は1000文字以内で入力してください"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <TextArea rows={8} placeholder="投稿内容を入力してください（画像のみでも投稿可）..." showCount maxLength={1000} style={{ minHeight: '200px', resize: 'vertical', fontFamily: 'inherit' }} autoSize={{ minRows: 8, maxRows: 20 }} />
          </Form.Item>
          <Form.Item label="画像・動画の添付 (任意, 100MBまで)">
            {imagePreview ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                {imageFile?.type.startsWith('video/') ? (
                  <video src={imagePreview} controls style={{ width: 150, border: '1px solid #d9d9d9', borderRadius: '4px' }} />
                ) : (
                  <Image src={imagePreview} alt="プレビュー" width={100} style={{ border: '1px solid #d9d9d9', borderRadius: '4px', padding: '4px' }}/>
                )}
                <Button danger onClick={handleRemoveImage}>削除</Button>
              </div>
            ) : (
              <Upload
                beforeUpload={handleImageChange}
                showUploadList={false}
                accept="video/*, image/*"
              >
                <Button icon={<UploadOutlined />}>ファイルを選択</Button>
              </Upload>
            )}
          </Form.Item>
          <Form.Item name="sendEmailNotification" valuePropName="checked" initialValue={false}>
            <Checkbox>他の掲示板利用者にもメールで投稿を連絡する</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BBS;
